import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import {
  pushSitesApi,
  templatePushSitesApi,
  type PushSite,
  type PushChannelLedger,
  type AuthType,
  ApiError,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TableShell, type ColumnSpec } from "@/components/TableShell";
import { InheritedModelConfigEditor } from "@/components/InheritedModelConfigEditor";
import { ApiTestConsole } from "@/components/ApiTestConsole";
import { ADAPTER_PROFILES, ADAPTER_CHOICES, missingBindingKey, requiredBindingKeysFor } from "@/adapters/pushProfiles";
import { probeKey, probeTime } from "@/lib/probe";

// ---------------------------------------------------------------------------
// /push-sites — 推送站点 CRUD(v6 B4.3)+ 接口测试面板。
// 后端 PushSite:site_type 固定 push。doc/011:站点无 api_contract —— 所有商户级
// 参数统一放绑定行 template_push_site.field_mapping,站点行只存凭证。
// 每行「测试」打开抽屉,按商家能力做接口测试(custom-ezlinkai-com 走真实 upstream,
// custom-push 显示「未接入」)。
// ---------------------------------------------------------------------------

type PushAuthType = AuthType; // token | basic (oauth2 removed — was unimplemented)

const emptyForm = {
  code: "",
  name: "",
  url: "",
  adapter_type: "custom-push",
  auth_type: "token" as PushAuthType,
  auth: "",
  user_name: "",
  remark: "",
  enabled: true,
  models: null as string | null,
  model_mapping: null as string | null,
  site_options: {} as Record<string, string>,
};

export function PushSitesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["push-sites"], queryFn: pushSitesApi.list });
  // 站点"缺配置"徽标依据绑定行 field_mapping(doc/011):拉取全部绑定,聚合该站
  // 是否已有某条 field_mapping 满足商家必填 key。
  const bindsQ = useQuery({ queryKey: ["template-push-sites"], queryFn: () => templatePushSitesApi.listAll(), staleTime: 30_000 });
  const pushFieldMappingBySite = useMemo(() => {
    const m = new Map<number, string[]>();
    (bindsQ.data?.items ?? []).forEach((b) => {
      const arr = m.get(b.site_id) ?? [];
      arr.push(b.field_mapping ?? "");
      m.set(b.site_id, arr);
    });
    return m;
  }, [bindsQ.data]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PushSite | null>(null);
  const [testing, setTesting] = useState<PushSite | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState<string | null>(null);
  const [creatingTest, setCreatingTest] = useState(false);
  const [createdTestedKey, setCreatedTestedKey] = useState<string | null>(null);
  const [ledgerTarget, setLedgerTarget] = useState<{ siteName: string; channelId: number } | null>(null);
  const channelIDsBySite = useMemo(() => {
    const m = new Map<number, number[]>();
    (bindsQ.data?.items ?? []).forEach((b) => {
      if (b.push_template_id > 0) m.set(b.site_id, Array.from(new Set([...(m.get(b.site_id) ?? []), b.push_template_id])));
    });
    return m;
  }, [bindsQ.data]);
  const ledgerMut = useMutation({
    mutationFn: ({ siteID, channelID }: { siteID: number; channelID: number }) => pushSitesApi.channelLedger(siteID, channelID),
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "查询渠道总账失败"),
  });

  const createMut = useMutation({
    mutationFn: () => pushSitesApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push-sites"] });
      setCreating(false);
      setForm(emptyForm);
      setMsg("创建成功");
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "创建失败"),
  });

  // 控制台需要已落库的站点 id + 已保存凭证,故「打开控制台」= 先保存、再打开。
  const handleCreateAndOpenConsole = async () => {
    if (!createTested) return;
    try {
      const site = await createMut.mutateAsync();
      setTesting(site);
    } catch {
      // createMut.onError 已展示错误信息
    }
  };

  const deleteMut = useMutation({
    mutationFn: (id: number) => pushSitesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push-sites"] });
      setMsg("删除成功");
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "删除失败"),
  });

  // 行级「测试」= 现有凭证探针(复用 /:id/templates 连通性探测,写入 last_tested),
  // 与中转站行测试按钮一致。doc/009 §2.2 已把 templates(id) 定为推送站现有凭证探针。
  const rowTestMut = useMutation({
    mutationFn: (id: number) => pushSitesApi.templates(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["push-sites"] });
      const at = r.tested_at ? probeTime(r.tested_at) : "—";
      setMsg(r.ok ? `连通性正常(${at})` : `失败(${at}): ${r.error ?? "未知"}`);
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "测试失败"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof pushSitesApi.update>[1] }) =>
      pushSitesApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push-sites"] });
      setEditing(null);
      setMsg("已保存");
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "保存失败"),
  });

  // doc/009: 新建「先测后存」。门禁只在可测适配器(custom-push 为无测试能力兜底)且
  // 有新凭证时生效;被测对象指纹 = adapter/url/auth_type/user_name/auth。
  // basic 认证要求 user_name + auth 都非空;token 只看 auth。
  const createProfile = ADAPTER_PROFILES[form.adapter_type];
  const createAuth = form.auth;
  const createCredComplete = form.auth_type === "basic"
    ? form.auth.trim() !== "" && form.user_name.trim() !== ""
    : form.auth.trim() !== "";
  const createNeedsTest = createCredComplete && !!createProfile;
  const createKey = probeKey(form.adapter_type, form.url, form.auth_type, form.user_name, createAuth);
  const createTested = createdTestedKey === createKey;

  const handleCreateTest = async () => {
    setMsg(null);
    if (!createProfile) {
      setCreatedTestedKey(null);
      setMsg("该适配器(custom-push)未接入测试能力,为新凭证兜底 stub,保存不受限。");
      return;
    }
    setCreatingTest(true);
    try {
      const r = await pushSitesApi.testCredentials({
        adapter_type: form.adapter_type,
        url: form.url,
        auth_type: form.auth_type,
        auth: form.auth,
        user_name: form.auth_type === "basic" ? form.user_name : undefined,
        site_options: form.site_options,
      });
      if (r.ok) {
        setCreatedTestedKey(createKey);
        setMsg(`新凭证测试通过(${probeTime(r.tested_at)})`);
      } else {
        setCreatedTestedKey(null);
        setMsg(`测试失败: ${r.error ?? "未知"}`);
      }
    } catch (e) {
      setCreatedTestedKey(null);
      setMsg(e instanceof ApiError ? e.message : "测试失败");
    } finally {
      setCreatingTest(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">推送站点</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">接收上游的自研推送站,凭证加密落库;商户级参数在「推送绑定」的 field_mapping 里配置。点行「测试」可对商家 API 做真实连通/创建测试。</p>
        </div>
        <Button onClick={() => { setCreating(true); setEditing(null); setMsg(null); setCreatedTestedKey(null); }}>+ 新建</Button>
      </div>

      {msg && <div className="text-sm text-muted-foreground">{msg}</div>}

      {creating && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">新建推送站点</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
              className="grid grid-cols-2 gap-3"
            >
              <Field label="编码 code" hint="站点稳定身份,表内唯一,仅字母数字,创建后不可改(≤50)。">
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required pattern="[a-zA-Z0-9]+" maxLength={50} placeholder="push-atlas-01" />
              </Field>
              <Field label="名称"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
              <Field label="URL"><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://downstream.example.com/api" required /></Field>
              <Field label="适配器(商家)">
                <select
                  className="h-10 rounded-md border bg-background px-3 text-sm"
                  value={form.adapter_type}
                  onChange={(e) => {
                    // 切换适配器时清空凭据:不同 adapter 的 auth_type 含义不同
                    // (basic 时 user_name + password,token 时单 token),运营必须重新输入。
                    const adapterType = e.target.value;
                    const nextProfile = ADAPTER_PROFILES[adapterType];
                    setForm((prev) => ({
                      ...prev,
                      adapter_type: adapterType,
                      auth_type: nextProfile?.allowedAuthType ?? prev.auth_type,
                      url: nextProfile?.baseUrl ?? prev.url,
                      auth: "",
                      user_name: "",
                      site_options: {},
                    }));
                  }}
                >
                  {ADAPTER_CHOICES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="认证方式">
                <select
                  className="h-10 rounded-md border bg-background px-3 text-sm"
                  value={form.auth_type}
                  onChange={(e) => {
                    // 切到 token 时清空 user_name;切到 basic 时把 user_name 留作空由用户填。
                    const next = e.target.value as PushAuthType;
                    setForm((prev) => ({
                      ...prev,
                      auth_type: next,
                      user_name: next === "basic" ? prev.user_name : "",
                    }));
                  }}
                >
                  {(createProfile?.allowedAuthType ? [createProfile.allowedAuthType] : (["token", "basic"] as const)).map((v) => (
                    <option key={v} value={v}>
                      {v === "token" ? "token" : "basic (账号 + 密码)"}
                    </option>
                  ))}
                </select>
              </Field>
              {form.auth_type === "basic" ? (
                <>
                  <Field label="账号 user_name(明文,落库前会加密)" hint={createProfile?.authFieldHint}>
                    <Input
                      type="text"
                      value={form.user_name}
                      onChange={(e) => setForm({ ...form, user_name: e.target.value })}
                      required
                      minLength={1}
                      placeholder="账号 user_name(如 ylsl0803)"
                    />
                  </Field>
                  <Field label="密码 password(明文,落库前会加密)">
                    <Input
                      type="password"
                      value={form.auth}
                      onChange={(e) => setForm({ ...form, auth: e.target.value })}
                      required
                      minLength={1}
                      placeholder="账号登录密码"
                    />
                  </Field>
                </>
              ) : (
                <Field label="凭证(明文,落库前会加密)" hint={createProfile?.authFieldHint}>
                  <Input type="text" value={form.auth} onChange={(e) => setForm({ ...form, auth: e.target.value })} required minLength={1} placeholder="token" />
                </Field>
              )}
              {createProfile?.showPlatformUserId && (
                <Field label="对应系统用户 Id(platform_user_id)" hint="newapi 系下游的 New-Api-User 头;0 或留空 = 不发送。">
                  <Input
                    value={form.site_options.platform_user_id ?? ""}
                    onChange={(e) => setForm({ ...form, site_options: { ...form.site_options, platform_user_id: e.target.value } })}
                    placeholder="留空 = 不发送"
                  />
                </Field>
              )}
              {createProfile?.siteOptionsFields?.map((f) => (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  <select
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    value={form.site_options[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, site_options: { ...form.site_options, [f.key]: e.target.value } })}
                  >
                    {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              ))}
              <Field label="备注" className="col-span-2">
                <Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
              </Field>
              <div className="col-span-2 rounded-lg border bg-muted/20 p-3">
                <InheritedModelConfigEditor
                  inheritable={false}
                  models={form.models}
                  modelMapping={form.model_mapping}
                  onModelsChange={(models) => setForm({ ...form, models })}
                  onModelMappingChange={(model_mapping) => setForm({ ...form, model_mapping })}
                />
              </div>
              <div className="col-span-2 space-y-1">
                {createNeedsTest ? (
                  createTested ? (
                    <p className="text-xs text-emerald-600">新凭证已测试通过,可保存</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">新凭证需先通过「测试」才能保存。</p>
                  )
                ) : (
                  !createProfile && <p className="text-xs text-muted-foreground">适配器(custom-push)无测试能力,保存不受限。</p>
                )}
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => { setCreating(false); setForm(emptyForm); setCreatedTestedKey(null); }}>取消</Button>
                  <Button type="button" variant="outline" disabled={creatingTest} onClick={handleCreateTest}>
                    {creatingTest ? "测试中…" : "测试"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!createTested || creatingTest || createMut.isPending}
                    onClick={handleCreateAndOpenConsole}
                    title={createTested ? "保存并打开接口测试控制台" : "请先通过「测试」"}
                  >
                    打开控制台
                  </Button>
                  <Button type="submit" disabled={createMut.isPending || creatingTest || (createNeedsTest && !createTested)}>
                    {createMut.isPending ? "提交中…" : "保存"}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading && <div className="text-sm text-muted-foreground">加载中…</div>}
          {error && <div className="text-sm text-destructive">加载失败: {String(error)}</div>}
          {data && (
            <PushSitesTable
              sites={data.items}
              fieldMappingBySite={pushFieldMappingBySite}
              onOpen={(s) => { setTesting(s); setEditing(null); }}
              onEdit={(s) => { setEditing(s); setCreating(false); setMsg(null); }}
              onTest={(id) => rowTestMut.mutate(id)}
              onDelete={(s) => { if (confirm(`删除 ${s.name}?`)) deleteMut.mutate(s.id); }}
              channelIDsBySite={channelIDsBySite}
              onLedger={(s, channelID) => { ledgerMut.reset(); setLedgerTarget({ siteName: s.name, channelId: channelID }); ledgerMut.mutate({ siteID: s.id, channelID }); }}
              testing={rowTestMut.isPending}
              deleting={deleteMut.isPending}
            />
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditPushSiteModal
          s={editing}
          saving={updateMut.isPending}
          onClose={() => { if (!updateMut.isPending) { setEditing(null); setMsg(null); } }}
          onSave={(body) => updateMut.mutate({ id: editing.id, body })}
          onOpenConsole={() => { setTesting(editing); setEditing(null); }}
        />
      )}

      {testing && <PushSiteTestDrawer s={testing} onClose={() => setTesting(null)} />}

      {ledgerTarget && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">101aix 渠道总账 · {ledgerTarget.siteName} · #{ledgerTarget.channelId}</CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setLedgerTarget(null)}>关闭</Button>
          </CardHeader>
          <CardContent>
            {ledgerMut.isPending && <p className="text-sm text-muted-foreground">实时查询中…</p>}
            {!ledgerMut.isPending && ledgerMut.data && (
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
                <LedgerValue label="结算模式" value={ledgerMut.data.settlement_mode} />
                <LedgerValue label="Key 数" value={ledgerMut.data.total_count.toLocaleString()} />
                <LedgerValue label={`额度(${ledgerMut.data.currency})`} value={ledgerMut.data.quota_total.toLocaleString()} />
                <LedgerValue label={`用量(${ledgerMut.data.currency})`} value={ledgerMut.data.usage_amount_total.toLocaleString()} />
                <LedgerValue label="未采样" value={ledgerMut.data.usage_unsampled_count.toLocaleString()} />
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">实时只读，不落库，也不计入单 Key 用量。</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PushSitesTable({
  sites,
  fieldMappingBySite,
  onOpen,
  onEdit,
  onTest,
  onDelete,
  channelIDsBySite,
  onLedger,
  testing,
  deleting,
}: {
  sites: PushSite[];
  fieldMappingBySite: Map<number, string[]>;
  onOpen: (s: PushSite) => void;
  onEdit: (s: PushSite) => void;
  onTest: (id: number) => void;
  onDelete: (s: PushSite) => void;
  channelIDsBySite: Map<number, number[]>;
  onLedger: (s: PushSite, channelID: number) => void;
  testing: boolean;
  deleting: boolean;
}) {
  const [enabledFilter, setEnabledFilter] = useState<"all" | "enabled" | "disabled">("all");
  const rows = useMemo(
    () => sites.filter((s) => enabledFilter === "all" || (enabledFilter === "enabled") === s.enabled),
    [sites, enabledFilter],
  );
  const missingKey = (s: PushSite): string | null => {
    const required = requiredBindingKeysFor(s.adapter_type);
    if (required.length === 0) return null;
    for (const fm of fieldMappingBySite.get(s.id) ?? []) {
      if (missingBindingKey(s.adapter_type, fm) === null) return null;
    }
    return required[0];
  };
  const columns: ColumnSpec<PushSite>[] = useMemo(
    () => [
      { key: "id", label: "ID", sortable: true },
      { key: "code", label: "编码", sortable: true },
      { key: "name", label: "名称", sortable: true },
      { key: "url", label: "URL" },
      { key: "adapter_type", label: "适配器", sortable: true },
      { key: "auth_hint", label: "凭证" },
      { key: "target_code", label: "目标代号" },
      { key: "binding", label: "绑定" },
      { key: "enabled", label: "启用", sortable: true },
      { key: "actions", label: "", align: "right" },
    ],
    [],
  );
  return (
    <TableShell
      mode="client"
      columns={columns}
      data={rows}
      emptyText={sites.length === 0 ? '暂无推送站,点击右上角"+ 新建"开始' : "无匹配推送站"}
      search={{
        placeholder: "搜索编码/名称/URL/目标代号",
        match: (s) => `${s.code} ${s.name} ${s.url} ${s.target_code}`,
      }}
      filters={
        <div className="flex items-center gap-1 text-xs">
          {(["all", "enabled", "disabled"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setEnabledFilter(v)}
              className={`rounded-md border px-2 py-1 transition-colors ${
                enabledFilter === v ? "border-primary bg-primary/5 text-primary" : "border-[#e4eaf1] text-muted-foreground hover:bg-muted/40"
              }`}
            >
              {v === "all" ? "全部" : v === "enabled" ? "启用" : "禁用"}
            </button>
          ))}
        </div>
      }
      rowKey={(s) => s.id}
      renderRow={(s) => (
        <tr
          className="group cursor-pointer border-b hover:bg-muted/30"
          onClick={() => onOpen(s)}
          title="点击打开接口测试控制台"
        >
          <td className="py-2">{s.id}</td>
          <td className="font-mono text-xs">{s.code}</td>
          <td className="font-medium text-primary group-hover:underline">{s.name}</td>
          <td className="text-xs text-muted-foreground">{s.url}</td>
          <td><Badge variant={ADAPTER_PROFILES[s.adapter_type] ? "success" : "muted"}>{s.adapter_type}</Badge></td>
          <td className="font-mono text-xs">
            {s.auth_type === "basic"
              ? s.user_name_hint
                ? <span title="账号 / 密码">{s.user_name_hint} / {s.auth_hint}</span>
                : <span className="text-destructive" title="basic 站点缺 user_name,分发会失败">⚠ {s.auth_hint} (缺账号)</span>
              : s.auth_hint}
          </td>
          <td className="font-mono text-xs">{s.target_code}</td>
          <td className="text-xs text-muted-foreground">
            {missingKey(s)
              ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800" title="该站绑定行 field_mapping 未含商家必填字段,分发会 pre-flight 失败;请到「推送绑定」配置">缺配置</span>
              : (fieldMappingBySite.get(s.id)?.length ? "✓" : "—")}
          </td>
          <td>{s.enabled ? <Badge variant="success">启用</Badge> : <Badge variant="muted">禁用</Badge>}</td>
          <td className="text-right space-x-1">
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onEdit(s); }}>
              <Pencil className="mr-1 h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" disabled={testing} title="现有凭证连通性测试" onClick={(e) => { e.stopPropagation(); onTest(s.id); }}>
              测试
            </Button>
            {s.adapter_type === "custom-101aixnet" && (channelIDsBySite.get(s.id) ?? []).map((channelID) => (
              <Button key={channelID} size="sm" variant="outline" title={`实时查询渠道 #${channelID} 总账`} onClick={(e) => { e.stopPropagation(); onLedger(s, channelID); }}>
                总账 #{channelID}
              </Button>
            ))}
            <Button size="sm" variant="destructive" disabled={deleting} onClick={(e) => { e.stopPropagation(); onDelete(s); }}>
              删除
            </Button>
          </td>
        </tr>
      )}
    />
  );
}

function LedgerValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono font-medium">{value}</div>
    </div>
  );
}

function Field({ label, children, className = "", hint }: { label: string; children: React.ReactNode; className?: string; hint?: string }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>}
    </div>
  );
}

function EditPushSiteModal({
  s,
  saving,
  onClose,
  onSave,
  onOpenConsole,
}: {
  s: PushSite;
  saving: boolean;
  onClose: () => void;
  onSave: (body: Parameters<typeof pushSitesApi.update>[1]) => void;
  onOpenConsole: () => void;
}) {
  const [form, setForm] = useState({
    name: s.name,
    url: s.url,
    adapter_type: s.adapter_type as string,
    auth_type: s.auth_type as PushAuthType,
    auth: "",
    user_name: s.user_name ?? "",
    enabled: s.enabled,
    remark: s.remark,
    models: s.models,
    model_mapping: s.model_mapping,
    site_options: { ...(s.site_options ?? {}) } as Record<string, string>,
  });
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testedKey, setTestedKey] = useState<string | null>(null);
  const [testInfo, setTestInfo] = useState<string | null>(null);

  // doc/009: 编辑「先测后存」。适配器(商家)可改,切换即纳入指纹——以「变更后
  // 组合(adapter/url/auth_type/user_name/auth)」重新验证。basic 时 user_name
  // 与现值不同、或 auth 非空视为「改了凭证」。
  const profile = ADAPTER_PROFILES[form.adapter_type];
  const adapterChanged = form.adapter_type !== s.adapter_type;
  const userNameChanged = form.auth_type === "basic"
    && form.user_name.trim() !== (s.user_name ?? "").trim();
  const credChanged = form.auth_type === "basic"
    ? form.auth.trim() !== "" || userNameChanged
    : form.auth.trim() !== "";
  const probeWithNewCredentials = credChanged || adapterChanged;
  const curKey = probeKey(form.adapter_type, form.url, form.auth_type, form.user_name, form.auth);
  const tested = probeWithNewCredentials && !!profile && testedKey === curKey;

  const handleTest = async () => {
    setErr(null);
    setTestInfo(null);
    if (!profile) {
      setErr(`适配器 ${form.adapter_type} 未接入测试能力,自定义凭证可直接保存。`);
      return;
    }
    setTesting(true);
    try {
      if (probeWithNewCredentials) {
        // basic 时两个字段都必填(后端同样校验);token 时只填 auth。
        if (form.auth.trim() === "" || (form.auth_type === "basic" && form.user_name.trim() === "")) {
          setErr(adapterChanged ? "切换适配器后必须填写并测试新凭证" : "basic 认证下账号与密码都需要填写才能测试");
          setTesting(false);
          return;
        }
        const r = await pushSitesApi.testCredentials({
          adapter_type: form.adapter_type,
          url: form.url,
          auth_type: form.auth_type,
          auth: form.auth,
          user_name: form.auth_type === "basic" ? form.user_name : undefined,
          site_options: form.site_options,
        });
        if (r.ok) {
          setTestedKey(curKey);
          setTestInfo(`新凭证测试通过(${probeTime(r.tested_at)})`);
        } else {
          setTestedKey(null);
          setErr(`新凭证测试失败: ${r.error ?? "未知"}`);
        }
      } else {
        const r = await pushSitesApi.templates(s.id);
        if (r.ok) {
          setTestInfo(`现有凭证测试通过(${probeTime(r.tested_at)})`);
        } else {
          setErr(`现有凭证测试失败: ${r.error ?? "未知"}`);
        }
      }
    } catch (e) {
      setTestedKey(null);
      setErr(e instanceof ApiError ? e.message : "测试失败");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">编辑推送站</h2>
          <span className="text-xs text-muted-foreground">原名:{s.name}</span>
        </div>
        {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setErr(null);
            const body: Parameters<typeof pushSitesApi.update>[1] = {
              name: form.name.trim(),
              url: form.url.trim(),
              adapter_type: form.adapter_type,
              auth_type: form.auth_type,
              enabled: form.enabled,
              remark: form.remark.trim(),
              models: form.models,
              model_mapping: form.model_mapping,
              site_options: form.site_options,
            };
            // 账号只有变化时提交，密码非空时提交。
            if (form.auth !== "") body.auth = form.auth;
            if (userNameChanged) body.user_name = form.user_name;
            if (form.auth_type === "token" && s.auth_type === "basic") body.user_name = "";
            onSave(body);
          }}
        >
          <Field label="名称"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="URL"><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required /></Field>
          <Field label="适配器(商家)" hint={`当前:${s.adapter_type}`}>
            <select
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={form.adapter_type}
              onChange={(e) => {
                // 同 create form:切换适配器清空凭据,防止跨 adapter 残留 user:pass。
                const adapterType = e.target.value;
                const nextProfile = ADAPTER_PROFILES[adapterType];
                setForm((prev) => ({
                  ...prev,
                  adapter_type: adapterType,
                  auth_type: nextProfile?.allowedAuthType ?? prev.auth_type,
                  url: nextProfile?.baseUrl ?? prev.url,
                  auth: "",
                  user_name: "",
                  site_options: {},
                }));
              }}
            >
              {ADAPTER_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="认证方式">
            <select
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={form.auth_type}
              onChange={(e) => {
                const next = e.target.value as PushAuthType;
                setForm((prev) => ({
                  ...prev,
                  auth_type: next,
                  user_name: next === "basic" ? prev.user_name : "",
                }));
              }}
            >
              {(profile?.allowedAuthType ? [profile.allowedAuthType] : (["token", "basic"] as const)).map((v) => (
                <option key={v} value={v}>
                  {v === "token" ? "token" : "basic (账号 + 密码)"}
                </option>
              ))}
            </select>
          </Field>
          {form.auth_type === "basic" ? (
            <>
              <Field label="账号 user_name" hint={profile?.authFieldHint ?? `当前账号提示:${s.user_name_hint || "—"}`}>
                <Input type="text" value={form.user_name} onChange={(e) => setForm({ ...form, user_name: e.target.value })} placeholder="账号 user_name" />
              </Field>
              <Field label="重置密码 password(留空 = 不改)" hint={`当前凭证提示:${s.auth_hint || "—"}`}>
                <Input type="password" value={form.auth} onChange={(e) => setForm({ ...form, auth: e.target.value })} placeholder="留空保持原值" />
              </Field>
            </>
          ) : (
            <Field label="重置凭证(留空 = 不改)" hint={profile?.authFieldHint ?? `当前凭证提示:${s.auth_hint || "—"}`}>
              <Input type="text" value={form.auth} onChange={(e) => setForm({ ...form, auth: e.target.value })} placeholder="留空保持原值" />
            </Field>
          )}
          <Field label="启用">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 rounded border" />
              {form.enabled ? "启用" : "禁用"}
            </label>
          </Field>
          {profile?.showPlatformUserId && (
            <Field label="对应系统用户 Id(platform_user_id)" hint="newapi 系下游的 New-Api-User 头;0 或留空 = 不发送。">
              <Input
                value={form.site_options.platform_user_id ?? ""}
                onChange={(e) => setForm({ ...form, site_options: { ...form.site_options, platform_user_id: e.target.value } })}
                placeholder="留空 = 不发送"
              />
            </Field>
          )}
          {profile?.siteOptionsFields?.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint}>
              <select
                className="h-10 rounded-md border bg-background px-3 text-sm"
                value={form.site_options[f.key] ?? ""}
                onChange={(e) => setForm({ ...form, site_options: { ...form.site_options, [f.key]: e.target.value } })}
              >
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          ))}
          {Object.keys(form.site_options)
            .filter((key) => key !== "platform_user_id" && !(profile?.siteOptionsFields ?? []).some((f) => f.key === key) && form.site_options[key] !== "")
            .map((key) => (
              <Field key={key} label={`未知站点选项:${key}`} hint="后端白名单不接受该键,保存会被拒绝。请确认后移除。">
                <div className="flex items-center gap-2">
                  <Input value={form.site_options[key]} readOnly />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const next = { ...form.site_options };
                      delete next[key];
                      setForm({ ...form, site_options: next });
                    }}
                  >
                    移除
                  </Button>
                </div>
              </Field>
            ))}
          <Field label="备注" className="col-span-2"><Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} /></Field>
          <div className="col-span-2 rounded-lg border bg-muted/20 p-3">
            <InheritedModelConfigEditor
              inheritable={false}
              models={form.models}
              modelMapping={form.model_mapping}
              onModelsChange={(models) => setForm({ ...form, models })}
              onModelMappingChange={(model_mapping) => setForm({ ...form, model_mapping })}
            />
          </div>
          <div className="col-span-2 space-y-1 pt-2">
            {probeWithNewCredentials ? (
              tested ? (
                <p className="text-xs text-emerald-600">新凭证已测试通过,可保存</p>
              ) : (
                <p className="text-xs text-muted-foreground">{adapterChanged ? "切换适配器后必须填写并测试新凭证。" : "新凭证需先通过「测试」才能保存;留空凭证(=不改)可直接保存。"}</p>
              )
            ) : (
              testInfo && <p className="text-xs text-emerald-600">{testInfo}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>取消</Button>
              <Button type="button" variant="outline" disabled={testing} onClick={handleTest}>
                {testing ? "测试中…" : "测试"}
              </Button>
              <Button type="button" variant="outline" onClick={onOpenConsole}>打开控制台</Button>
              <Button type="submit" disabled={saving || testing || (probeWithNewCredentials && !!profile && testedKey !== curKey)}>
                {saving ? "保存中…" : "保存"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function PushSiteTestDrawer({ s, onClose }: { s: PushSite; onClose: () => void }) {
  const profile = ADAPTER_PROFILES[s.adapter_type];
  const hint = /sk-/.test(s.auth_hint || "") ? "sk-••••••" : s.auth_hint || "••••••";
  return (
    <ApiTestConsole
      title={`接口测试控制台 · ${s.name}`}
      adapterType={s.adapter_type}
      url={s.url}
      supported={!!profile}
      unsupportedNote={<>适配器 <code>{s.adapter_type}</code> 未接入多端点测试。</>}
      profile={profile}
      authHeaderLine={profile?.authHeaderLine(hint) ?? ""}
      probe={async () => {
        const result = await pushSitesApi.templates(s.id);
        return {
          data: { code: 0, ok: result.ok, tested_at: result.tested_at, data: result.templates ?? [] },
          toast: `发送成功，模板 ${result.templates?.length ?? 0} 个`,
        };
      }}
      proxy={(body) => pushSitesApi.proxy(s.id, body)}
      onClose={onClose}
    />
  );
}
